import type { GeoLocation, TelegramWebApp } from '../types';

export type LocationErrorCode = 'unsupported' | 'denied' | 'unavailable';

export class LocationError extends Error {
  /**
   * @param fromTelegram true when Telegram refused for this bot: the user can only re-allow it
   * in the bot's Telegram settings (see openTelegramLocationSettings), Telegram won't ask again.
   */
  constructor(public code: LocationErrorCode, message?: string, public fromTelegram = false) {
    super(message ?? code);
    this.name = 'LocationError';
  }
}

const TIMEOUT_MS = 15000;

function getTelegramWebApp(): TelegramWebApp | undefined {
  return (window as any).Telegram?.WebApp as TelegramWebApp | undefined;
}

function isInsideTelegram(): boolean {
  const webApp = getTelegramWebApp();
  return !!webApp?.initData && webApp.initData.trim().length > 0;
}

/**
 * Telegram Bot API 8.0+ LocationManager. Returns null when not available so the caller
 * can fall back to the browser Geolocation API.
 */
function getTelegramLocation(): Promise<GeoLocation | null> {
  const webApp = getTelegramWebApp();
  const manager = webApp?.LocationManager;
  if (!manager || (webApp?.isVersionAtLeast && !webApp.isVersionAtLeast('8.0'))) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new LocationError('unavailable')), TIMEOUT_MS);
    const done = (fn: () => void) => {
      window.clearTimeout(timer);
      fn();
    };

    const request = () => {
      if (!manager.isLocationAvailable) {
        done(() => resolve(null));
        return;
      }
      if (manager.isAccessRequested && !manager.isAccessGranted) {
        done(() => reject(new LocationError('denied', undefined, true)));
        return;
      }
      manager.getLocation(data => {
        if (!data) {
          done(() => reject(new LocationError('denied', undefined, true)));
          return;
        }
        done(() =>
          resolve({
            latitude: data.latitude,
            longitude: data.longitude,
            accuracyMeters: data.horizontal_accuracy,
            timestamp: Date.now(),
          })
        );
      });
    };

    if (manager.isInited) {
      request();
    } else {
      manager.init(request);
    }
  });
}

function getBrowserLocation(): Promise<GeoLocation> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.reject(new LocationError('unsupported'));
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy,
          timestamp: position.timestamp || Date.now(),
        }),
      error =>
        reject(new LocationError(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable', error.message)),
      { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 0 }
    );
  });
}

/**
 * Current device location. Inside Telegram the native LocationManager is preferred;
 * otherwise (or when it is unavailable) the browser Geolocation API is used. When Telegram
 * refuses (location not allowed for this bot), the browser API is tried before giving up.
 */
export async function getCurrentLocation(): Promise<GeoLocation> {
  if (!isInsideTelegram()) {
    return getBrowserLocation();
  }

  let telegramError: LocationError | null = null;
  try {
    const telegramLocation = await getTelegramLocation();
    if (telegramLocation) {
      return telegramLocation;
    }
  } catch (error) {
    if (!(error instanceof LocationError)) throw error;
    telegramError = error;
  }

  try {
    return await getBrowserLocation();
  } catch (browserError) {
    // Report Telegram's refusal: that's the one the user can fix (in the bot's settings)
    throw telegramError ?? browserError;
  }
}

/** True when Telegram can open the screen where location is allowed for this bot. */
export function canOpenTelegramLocationSettings(): boolean {
  const webApp = getTelegramWebApp();
  return isInsideTelegram() && !!webApp?.LocationManager?.isInited && typeof webApp.LocationManager.openSettings === 'function';
}

/** Opens Telegram's location settings for this bot. Must be called from a user tap. */
export function openTelegramLocationSettings(): boolean {
  if (!canOpenTelegramLocationSettings()) return false;
  getTelegramWebApp()!.LocationManager!.openSettings();
  return true;
}
